import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowRight, ArrowLeft, X, Target, BookOpen, Coins, Trophy } from 'lucide-react'

interface TutorialStep {
  id: number
  title: string
  description: string
  icon: React.ReactNode
  highlight?: string
  action?: string
}

interface TutorialOverlayProps {
  isOpen: boolean
  onClose: () => void
  onComplete: () => void
}

const tutorialSteps: TutorialStep[] = [
  {
    id: 1,
    title: "Bienvenue dans Réminiscence !",
    description: "Un jeu où vos souvenirs deviennent des histoires magiques. Vous allez apprendre à jouer en 4 étapes simples.",
    icon: <BookOpen className="w-8 h-8" />,
    action: "Commençons l'aventure !"
  },
  {
    id: 2,
    title: "Objectif : Créer 1 ligne de 4",
    description: "Votre but ultime est d'aligner 4 jetons de votre couleur (horizontal, vertical ou diagonal). Le premier à créer cet alignement gagne instantanément la partie !",
    icon: <Target className="w-8 h-8" />,
    highlight: "plateau",
    action: "Compris ! Et comment je place mes cartes ?"
  },
  {
    id: 3,
    title: "Racontez et Entraidez-vous",
    description: "Pour jouer une action, vous devez dépenser 1 Jeton et raconter une histoire. Astuce : Comment gagner des jetons ? En aidant vos adversaires ! Si un joueur échoue à trouver un souvenir, mimez-le pour lui. La Banque vous récompensera d'un Jeton.",
    icon: <Coins className="w-8 h-8" />,
    highlight: "narrative",
    action: "Génial ! Et les jetons, à quoi ça sert ?"
  },
  {
    id: 4,
    title: "Gérez vos jetons intelligemment",
    description: "Vous commencez avec 7 jetons. Chaque action coûte des jetons. Si vous n'en avez plus, vous êtes en 'Dernier Souffle' : une dernière chance de jouer ! Économisez-les bien.",
    icon: <Trophy className="w-8 h-8" />,
    highlight: "tokens",
    action: "Parfait ! Je suis prêt à jouer !"
  }
]

const TutorialOverlay: React.FC<TutorialOverlayProps> = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0)

  const nextStep = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      onComplete()
    }
  }

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const currentTutorial = tutorialSteps[currentStep]

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay avec même style que la landing page */}
          <motion.div
            className="fixed inset-0 z-50"
            style={{
              background: `
                radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.8) 100%),
                url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.08'/%3E%3C/svg%3E"),
                #0a192f
              `,
              backgroundBlendMode: 'overlay, normal, normal'
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Cadre Art Déco pour cohérence */}
          <div className="fixed inset-0 z-50 pointer-events-none">
            <div 
              className="absolute inset-4"
              style={{
                border: '1px solid #d4af37',
                borderRadius: '2px'
              }}
            />
            {/* Coins ornementés */}
            <div 
              className="absolute top-2 left-2 w-8 h-8"
              style={{
                background: `
                  linear-gradient(45deg, transparent 30%, #d4af37 30%, #d4af37 32%, transparent 32%),
                  linear-gradient(-45deg, transparent 30%, #d4af37 30%, #d4af37 32%, transparent 32%)
                `,
                borderTop: '2px solid #d4af37',
                borderLeft: '2px solid #d4af37'
              }}
            />
            <div 
              className="absolute top-2 right-2 w-8 h-8"
              style={{
                background: `
                  linear-gradient(135deg, transparent 30%, #d4af37 30%, #d4af37 32%, transparent 32%),
                  linear-gradient(-135deg, transparent 30%, #d4af37 30%, #d4af37 32%, transparent 32%)
                `,
                borderTop: '2px solid #d4af37',
                borderRight: '2px solid #d4af37'
              }}
            />
            <div 
              className="absolute bottom-2 left-2 w-8 h-8"
              style={{
                background: `
                  linear-gradient(-45deg, transparent 30%, #d4af37 30%, #d4af37 32%, transparent 32%),
                  linear-gradient(45deg, transparent 30%, #d4af37 30%, #d4af37 32%, transparent 32%)
                `,
                borderBottom: '2px solid #d4af37',
                borderLeft: '2px solid #d4af37'
              }}
            />
            <div 
              className="absolute bottom-2 right-2 w-8 h-8"
              style={{
                background: `
                  linear-gradient(-135deg, transparent 30%, #d4af37 30%, #d4af37 32%, transparent 32%),
                  linear-gradient(135deg, transparent 30%, #d4af37 30%, #d4af37 32%, transparent 32%)
                `,
                borderBottom: '2px solid #d4af37',
                borderRight: '2px solid #d4af37'
              }}
            />
          </div>

          {/* Modal de tutoriel redesignée */}
          <motion.div
            className="fixed inset-0 flex items-center justify-center z-50 p-8"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden border-2 border-amber-200/30">
              {/* Header redesigné avec style mystérieux */}
              <div 
                className="relative p-8 text-white overflow-hidden"
                style={{
                  background: `
                    linear-gradient(135deg, rgba(10, 25, 47, 0.95) 0%, rgba(30, 58, 138, 0.95) 100%),
                    url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.1'/%3E%3C/svg%3E")
                  `,
                  backgroundBlendMode: 'overlay, normal'
                }}
              >
                {/* Bouton fermer redesigné */}
                <motion.button
                  onClick={onClose}
                  className="absolute top-6 right-6 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/20 transition-all duration-300"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <X className="w-5 h-5 text-white/80" />
                </motion.button>
                
                {/* Contenu du header */}
                <div className="flex items-center space-x-6">
                  <motion.div 
                    className="w-20 h-20 bg-gradient-to-br from-amber-400/20 to-amber-600/20 rounded-full flex items-center justify-center border-2 border-amber-400/30 backdrop-blur-sm"
                    animate={{ rotate: [0, 5, -5, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <span className="text-3xl">{currentStep === 0 ? '🎭' : currentStep === 1 ? '🎯' : currentStep === 2 ? '💰' : '🏆'}</span>
                  </motion.div>
                  <div>
                    <motion.h2 
                      className="text-3xl font-serif font-bold mb-2 text-amber-100"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      {currentTutorial.title}
                    </motion.h2>
                    <p className="text-white/70 text-lg">
                      Chapitre {currentStep + 1} sur {tutorialSteps.length}
                    </p>
                  </div>
                </div>

                {/* Barre de progression dorée */}
                <div className="mt-6 bg-white/10 rounded-full h-3 backdrop-blur-sm border border-white/20">
                  <motion.div
                    className="bg-gradient-to-r from-amber-400 to-amber-500 rounded-full h-3 shadow-lg"
                    initial={{ width: 0 }}
                    animate={{ width: `${((currentStep + 1) / tutorialSteps.length) * 100}%` }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                  />
                </div>
              </div>

              {/* Contenu redesigné */}
              <div className="p-8 bg-gradient-to-br from-white/50 to-amber-50/30">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                >
                  <div className="text-center mb-8">
                    <p className="text-xl text-slate-700 leading-relaxed font-light">
                      {currentTutorial.description}
                    </p>
                  </div>

                  {/* Carte d'illustration ludique */}
                  {currentTutorial.highlight && (
                    <motion.div 
                      className="bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-2xl p-8 mb-8 border-2 border-amber-200/50 backdrop-blur-sm shadow-lg"
                      initial={{ scale: 0.95, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.2, duration: 0.4 }}
                    >
                      <div className="flex items-start space-x-4">
                        <motion.div 
                          className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg flex-shrink-0"
                          animate={{ rotate: [0, 10, -10, 0] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                        >
                          <span className="text-white font-bold text-lg">💡</span>
                        </motion.div>
                        <div className="flex-1">
                          <h4 className="text-amber-800 font-bold text-lg mb-2">À retenir :</h4>
                          <p className="text-amber-700 font-medium leading-relaxed">
                            {currentTutorial.highlight === 'plateau' && "🎯 Alignez 4 jetons sur ce plateau 7×7 pour gagner instantanément !"}
                            {currentTutorial.highlight === 'narrative' && "🤝 Racontez ET aidez les autres - c'est la clé pour gagner des jetons !"}
                            {currentTutorial.highlight === 'tokens' && "💰 Chaque action coûte 1 jeton - économisez et gagnez par l'entraide !"}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Conseils ludiques selon l'étape */}
                  <motion.div 
                    className="bg-white/60 rounded-xl p-6 border border-slate-200/50"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <span className="text-2xl">
                        {currentStep === 0 ? '🌟' : currentStep === 1 ? '🎯' : currentStep === 2 ? '✨' : '🎉'}
                      </span>
                      <h4 className="text-slate-800 font-semibold">
                        {currentStep === 0 ? 'Conseil du Gardien' : currentStep === 1 ? 'Stratégie' : currentStep === 2 ? 'Astuce' : 'Bravo !'}
                      </h4>
                    </div>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      {currentStep === 0 && "Laissez-vous porter par la magie des souvenirs. Chaque histoire compte !"}
                      {currentStep === 1 && "Une seule ligne de 4 suffit ! Horizontal, vertical ou diagonal - soyez le premier !"}
                      {currentStep === 2 && "L'entraide rapporte ! Aidez vos adversaires et la Banque vous récompensera."}
                      {currentStep === 3 && "Vous êtes prêt à devenir un véritable Gardien de la Mémoire !"}
                    </p>
                  </motion.div>
                </motion.div>
              </div>

              {/* Footer redesigné avec style cohérent */}
              <div 
                className="px-8 py-6 flex items-center justify-between"
                style={{
                  background: 'linear-gradient(135deg, rgba(10, 25, 47, 0.1) 0%, rgba(30, 58, 138, 0.1) 100%)'
                }}
              >
                {/* Bouton Précédent */}
                <motion.button
                  onClick={prevStep}
                  disabled={currentStep === 0}
                  className="flex items-center space-x-3 px-6 py-3 bg-white/50 text-slate-700 rounded-lg hover:bg-white/70 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-300 backdrop-blur-sm border border-slate-200/50"
                  whileHover={currentStep > 0 ? { scale: 1.02, y: -1 } : {}}
                  whileTap={currentStep > 0 ? { scale: 0.98 } : {}}
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="font-medium">Précédent</span>
                </motion.button>

                {/* Indicateurs de progression dorés */}
                <div className="flex space-x-3">
                  {tutorialSteps.map((_, index) => (
                    <motion.div
                      key={index}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index <= currentStep 
                          ? 'bg-gradient-to-r from-amber-400 to-amber-500 shadow-lg' 
                          : 'bg-slate-300/50 border border-slate-300'
                      }`}
                      initial={{ scale: 0.8 }}
                      animate={{ 
                        scale: index === currentStep ? 1.2 : 1,
                        boxShadow: index === currentStep ? '0 0 20px rgba(245, 158, 11, 0.5)' : 'none'
                      }}
                      transition={{ duration: 0.3 }}
                    />
                  ))}
                </div>

                {/* Bouton Suivant */}
                <motion.button
                  onClick={nextStep}
                  className="flex items-center space-x-3 px-8 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all duration-300 shadow-lg font-bold"
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>{currentTutorial.action || "Continuer"}</span>
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default TutorialOverlay
